import { useEffect, useState, useMemo } from "react";
import FirebaseService from "../../services/firebase/FirebaseService";
import useParamsSelector, { getStudent, getAssignment } from "../../selectors";
import Submission from "../../interfaces/Submission";
import Table from "../../components/Screens/Teacher/Table/Table";
import { formatLastLogin } from "../../components/Screens/Teacher/Table/TableSortAndFormatFunctions";
import "./styles.scss";
import Loading from "../Loading/Loading";
import { accuracyToBand, accuracyToColor } from "../../utils/AccuracyToBand";

const ReadingReport = () => {
  const assignment = useParamsSelector(getAssignment);
  const student = useParamsSelector(getStudent);
  const [submission, setSubmission] = useState<Submission | null>(null);

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!(student && assignment)) return;
      const submissionData = await FirebaseService.getSubmission(student?.uid, assignment.assignmentDetails.bookId);
      setSubmission(submissionData);
    };
    fetchSubmission();
  });

  const submissionColumns = useMemo(
    () => [
      {
        // completion % column
        Header: "Completion",
        accessor: (data: any) => data.completionPercentage,
        disableSortBy: true,
        Cell: ({ value }: any) => (isNaN(value) ? "N/A" : Math.trunc(value * 100).toString() + "%"),
      },
      {
        // wpm column
        Header: "WPM",
        accessor: (data: any) => data.wordsPerMin,
        disableSortBy: true,
        Cell: (props: any) => (isNaN(props.value) ? "N/A" : Math.trunc(props.value)),
      },
      {
        // accuracy column
        Header: "Accuracy",
        accessor: (data: any) => data.accuracy,
        disableSortBy: true,
        // cell is what displays in the table, so put svg circle here
        // use accuracyToColor to get the appropriate color
        Cell: (props: any) =>
          props.value || props.value === 0 ? (
            <div className="accuracy-container">
              <svg height="16" width="16">
                <circle cx="8" cy="8" r="8" fill={accuracyToColor(props.value)} />
              </svg>
              <div className="accuracy-popup">
                {accuracyToBand(props.value)}
                <div className="caret"></div>
              </div>
            </div>
          ) : (
            ""
          ),
      },
      {
        // reading time column
        Header: "Minutes",
        accessor: (data: any) => data.totalTime,
        disableSortBy: true,
        // cell is what displays in the table, so use formatted time read
        Cell: (props: any) => {
          // if student data doesn't exist, return empty string
          // else return formatted time (include check for zero)
          if (isNaN(props.value)) {
            return "N/A";
          }
          const mins = Math.trunc(props.value / 60);
          const seconds = Math.trunc(props.value % 60);
          return `${mins}:${seconds < 10 ? "0" + seconds.toString() : seconds}`;
        },
      },
      {
        // total words column
        Header: "Words",
        accessor: (data: any) => data.totalWordsRead,
        disableSortBy: true,
        Cell: (props: any) => props.value ?? "N/A",
      },
      {
        // last login column
        Header: "Turned In",
        // If assignment turned in, return time of submission
        accessor: (data: any) => (data.turnedIn ? data.timeSubmitted : undefined),
        disableSortBy: true,
        Cell: formatLastLogin,
      },
    ],
    [],
  );

  return (
    <div className="ReadingReport">
      {!submission ? <Loading /> : <Table kind="assignment" columns={submissionColumns} data={[submission]} />}
    </div>
  );
};

export default ReadingReport;
